/**
 * ArHeaderCheckbox Component
 *
 * Select-all checkbox in the table header, scoped to `uiState.selectableRowIds`
 * — the rows the split, the column filters and the visibility view left on this
 * page, plus every rendered bundle header's members. Never the whole loaded
 * `rows` collection (SYD-2386).
 *
 * Select-all REPLACES the selection rather than accumulating onto it: the tick
 * set and the checked/indeterminate math then read the same list, so the toggle
 * is exactly reversible and no selection outside the visible rows can leave the
 * box stuck indeterminate.
 *
 * The visible set and the checked/indeterminate math come from
 * `resolveSelectionScope`, the same derivation the bulk actions read, so the
 * `AR_TABLE_PAGE_SIZE` backstop cannot drift between the box and what it ticks.
 *
 * Disabled while `isDispatchingWorkflowRun` is set: the per-recipient documents a
 * workflow run is building read the live ticks, so a select-all landing mid-run
 * would widen every document built after it (SYD-2410). `ArTableHeader` renders no
 * checkbox at all in PDF export mode, so the portal's own copies never see the lock.
 */

import { FC, memo, CSSProperties, useContext } from 'react';
import { Checkbox, theme } from 'antd';
import { useSnapshot } from 'valtio';
import { ArTableUIStateContext } from '../context';
import { formStore } from '@/store';
import { clearSelection } from '@/workflows/runtime/arTableSelection';
import { resolveSelectionScope } from './resolveSelectionScope';

type ArHeaderCheckboxProps = {
  stickyFirstColumn?: boolean;
};

/**
 * Header checkbox for select-all functionality.
 * Shows indeterminate state when some (but not all) selectable rows are selected.
 */
const ArHeaderCheckbox: FC<ArHeaderCheckboxProps> = ({
  stickyFirstColumn = true,
}) => {
  const { token } = theme.useToken();
  const uiState = useContext(ArTableUIStateContext)!;
  const selectableSnap = useSnapshot(uiState.selectableRowIds);
  const selectionSnap = useSnapshot(uiState.selection);
  const selectedRowsSnap = useSnapshot(uiState.selection.selectedRows);
  const { isDispatchingWorkflowRun } = useSnapshot(formStore.state);

  const { visibleRowIds: targetIds, selectedVisibleRowIds, everyVisibleTicked } =
    resolveSelectionScope({
      selectableRowIds: selectableSnap,
      selectedRows: selectedRowsSnap,
      scope: selectionSnap.scope,
    });
  const allSelected = everyVisibleTicked;
  const someSelected = selectedVisibleRowIds.length > 0 && !allSelected;

  /**
   * Toggles select-all, replacing the selection outright rather than adding to
   * it. Mutates the Valtio proxy directly — the intended pattern.
   */
  const handleClick = () => {
    if (targetIds.length === 0 || isDispatchingWorkflowRun) return;
    const selection = uiState.selection;

    clearSelection(selection);
    if (allSelected) return;
    for (const rowId of targetIds) {
      selection.selectedRows[rowId] = true;
      selection.selectedRowIndexById[rowId] = selection.selectedRowIds.length;
      selection.selectedRowIds.push(rowId);
    }
    selection.selectedCount = selection.selectedRowIds.length;
  };

  const cellStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: token.colorBgElevated,
    boxSizing: 'border-box',
    zIndex: 10,
  };

  if (stickyFirstColumn) {
    cellStyle.position = 'sticky';
    cellStyle.left = 0;
    cellStyle.zIndex = 20;
  }

  return (
    <div
      role="columnheader"
      aria-label="artable-header-checkbox"
      style={cellStyle}
      onClick={handleClick}
    >
      <Checkbox
        checked={allSelected}
        indeterminate={someSelected}
        disabled={targetIds.length === 0 || isDispatchingWorkflowRun}
        aria-label="select-all-rows"
      />
    </div>
  );
};

export default memo(ArHeaderCheckbox);
